import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { put } from 'helpers/apiHelpers';
import Close from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Button from 'components/CustomButtons/Button';
import CustomInput from 'components/CustomInput/CustomInput';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/styles';
import InfoIcon from 'assets/img/InfoIcon';
import { dangerColor } from 'assets/jss/material-dashboard-pro-react';
import RegularCheckbox from 'components/Checkbox/RegularCheckbox';
import { FormLabel } from '@material-ui/core';

import { orderStyles } from '../../../styles';

const useStyles = makeStyles({
  closeButton: orderStyles.closeButton,
  checkbox: { padding: '0', marginRight: '8px' },
  title: {
    margin: '18px 0 24px 0',
    fontWeight: '500',
    textAlign: 'center',
  },
  pointsRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  pointsLabel: {
    fontSize: '14px',
    fontWeight: '400',
    textAlign: 'center',
  },
  pointsInputWrapper: {
    marginLeft: '10px',
  },
  pointsInput: {
    textAlign: 'center !important',
  },
  mailNotifyRow: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    margin: '24px 0 32px 0',
  },
  mailNotifyLabel: {
    cursor: 'pointer',
    margin: '0 0 0 15px',
  },
  actionsRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  cancelButton: {
    marginRight: '5px',
    width: '25%',
  },
  saveButton: {
    marginLeft: '5px',
    width: '25%',
  },
  infoWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoText: {
    marginLeft: '8px',
    fontSize: '14px',
    textAlign: 'center',
    fontWeight: 400,
  },
});

const DeleteBagConfirmModal = ({
  isOpened,
  isLoading,
  returnCost = 0,
  toggleModal,
  dates = [],
  dietInfo = {},
  setIsLoading,
  setCalendarNeedsUpdate,
  paidMutation = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [overrideReturnedPoints, setOverrideReturnedPoints] = useState(0);
  const [sendEmail, setSendEmail] = useState(true);

  const parsePoints = value =>
    parseFloat(String(value ?? 0).replace(',', '.')) || 0;

  useEffect(() => {
    setOverrideReturnedPoints(returnCost);
  }, [returnCost]);

  const handleSubmit = () => {
    setIsLoading(true);
    put(`/v2/calendar/${dietInfo?.id}/cancel-bags`, {
      dates,
      sendMail: sendEmail,
      paidMutation,
      ...(paidMutation
        ? {
            overrideReturnedPoints:
              overrideReturnedPoints === returnCost
                ? null
                : parsePoints(overrideReturnedPoints),
          }
        : {}),
      ...(paidMutation ? { acceptedReturnCost: parsePoints(returnCost) } : {}),
    })
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(() => {
        try {
          toast.success(
            paidMutation
              ? t('singleDayDetails.removeAndReturnPointsConfirmModal')
              : t('singleDayDetails.removeBagConfirmModal')
          );
          toggleModal(false);
          setIsLoading(false);
          setCalendarNeedsUpdate(true);
        } catch (e) {
          // eslint-disable-next-line no-console -- FIXME
          console.log({ e });
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .catch(({ response }) => {
        switch (response?.status) {
          case 402:
            toast.info(
              t('singleDayDetails.bagDeletingIsImpossible.description')
            );
            break;
          default:
            break;
        }
        setIsLoading(false);
      });
  };

  return (
    <Dialog maxWidth="sm" open={isOpened} onClose={() => toggleModal(false)}>
      <DialogContent>
        <Button
          className={classes.closeButton}
          color="transparent"
          justIcon={true}
          round={true}
          onClick={() => {
            toggleModal(false);
          }}
        >
          <Close />
        </Button>
        <h1 className={classes.title}>
          {t('singleDayDetails.deleteBagConfirmModal.title')}
        </h1>
        {paidMutation ? (
          <div className={classes.pointsRow}>
            <h3 className={classes.pointsLabel}>
              {t('singleDayDetails.deleteBagConfirmModal.pointsToReturn')}
            </h3>
            <div className={classes.pointsInputWrapper}>
              <CustomInput
                formControlProps={{ fullWidth: true }}
                inputProps={{
                  value: overrideReturnedPoints,
                  onChange: e => {
                    setOverrideReturnedPoints(e.target.value);
                  },
                  type: 'number',
                  className: classes.pointsInput,
                }}
              />
            </div>
          </div>
        ) : (
          <div className={classes.infoWrapper}>
            <InfoIcon styles={{ color: dangerColor[0] }} />
            <span className={classes.infoText}>
              {t(
                'singleDayDetails.deleteBagConfirmModal.changeCostsNotBeIncluded'
              )}
            </span>
          </div>
        )}
        <div
          className={classes.mailNotifyRow}
          onClick={() => {
            setSendEmail(prev => !prev);
          }}
        >
          <RegularCheckbox
            id="sendEmailCheckbox"
            classes={{ root: classes.checkbox }}
            name="sendEmail"
            checked={sendEmail}
            onClick={() => {
              setSendEmail(prev => !prev);
            }}
          />
          <FormLabel htmlFor="sendEmailCheckbox">
            {t('singleDayDetails.deleteBagConfirmModal.mailNotify')}
          </FormLabel>
        </div>

        <div className={classes.actionsRow}>
          <Button
            className={classes.cancelButton}
            onClick={() => {
              toggleModal(false);
            }}
          >
            {t('common.shared.cancel')}
          </Button>
          <Button
            color="danger"
            className={classes.saveButton}
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {t('common.shared.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteBagConfirmModal;
